import { useCallback } from "react";

import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import CircularProgress from "components/circular-progress";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import {
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED
} from "constants/staff-tasks";
import DueDateDisplay from "features/staff-tasks-v2/drawer/staff-task-card/due-date-display.component";
import TaskStatusField from "features/staff-tasks-v2/drawer/staff-task-card/task-status-field-component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import type { StaffTask } from "services/types/staff-tasks.type";

import { AssignedTaskTags } from "components/assigned-tags";
import FieldLabel from "../../../drawer/staff-task-card/field-label.component";
import PriorityLabel from "../../priority-label.component";
import StaffTaskAttachments from "../staff-task-attachments";
import StaffTaskComments from "./staff-task-comments";
import TaskFields from "./task-fields.component";
import { formatBackendDateOnly } from "utils/common";

type ViewStaffTaskFormProps = {
  task: StaffTask | undefined;
  isBusy?: boolean;
};

export default function ViewStaffTaskForm({
  task,
  isBusy
}: ViewStaffTaskFormProps) {
  const { handleOpenTaskModal, handleStatusUpdate } = useStaffTasksV2();

  const handleStatusChange = useCallback(
    (code: StatusTransitionCode) => handleStatusUpdate(Number(task?.id), code),
    [handleStatusUpdate, task?.id]
  );

  if (!task) {
    return <CircularProgress sx={{ width: "100%" }} />;
  }

  const dueDateDisplay = task?.due_at
    ? formatBackendDateOnly(task.due_at, "MMM dd, yyyy")
    : null;

  const isComplete =
    task.status === TASK_STATUS_COMPLETED ||
    task.status === TASK_STATUS_CANCELLED;

  return (
    <Stack direction="row" mt={-1}>
      <Stack flex={5.5} sx={{ boxSizing: "border-box", pr: 2 }}>
        <Stack direction="row" columnGap={5}>
          <Stack rowGap={1.5}>
            <FieldLabel label="Priority" sx={{ mb: "3px" }} />
            <PriorityLabel priority={Number(task.priority)} />
          </Stack>
          <Stack rowGap={1.5} width="180px">
            <FieldLabel label="Status" />
            <TaskStatusField
              value={task.status}
              completedAtDate={
                task.status === TASK_STATUS_COMPLETED
                  ? task.completed_at
                  : undefined
              }
              onChange={handleStatusChange}
            />
          </Stack>

          <AssignedTaskTags task={task} />

          <Stack rowGap={1.5}>
            <FieldLabel label="Due Date" sx={{ pb: "2px" }} />
            <DueDateDisplay
              date={task.due_at}
              showDate={isComplete}
              isComplete={isComplete}
              sx={{ mr: -1 }}
            />
            {!isComplete && dueDateDisplay && (
              <Typography
                component="div"
                variant="body1"
                sx={{
                  color: grey[900],
                  fontSize: "12px",
                  mt: -1,
                  display: "flex",
                  alignSelf: "flex-start",
                  pl: 2
                }}
              >
                {dueDateDisplay}
              </Typography>
            )}
          </Stack>
          <Button
            variant="contained"
            startIcon={
              isBusy ? (
                <CircularProgress sx={{ transform: "scale(0.5)" }} />
              ) : (
                <EditIcon />
              )
            }
            sx={{ height: "38px", mt: 3.2 }}
            disabled={isBusy}
            onClick={() => {
              handleOpenTaskModal({
                mode: "edit",
                taskId: task.id,
                onCancelMode: "openViewModal"
              });
            }}
          >
            Edit
          </Button>
        </Stack>
        <Stack rowGap={2} mt={2}>
          <TaskFields task={task} />
        </Stack>
        <Box mt={2}>
          <StaffTaskAttachments
            taskId={task.id}
            attachments={task.attachments}
          />
        </Box>
      </Stack>
      <Box display="flex" flex={5}>
        <StaffTaskComments task={task} isBusy={isBusy} />
      </Box>
    </Stack>
  );
}
